import React from 'react';
import { Box, Divider, Grid2, MenuItem, Typography } from '@mui/material';
import { IFontMarginTitleTheme, IFontTheme, IMarginTheme, ITitleTheme, ThemeValue } from '../../interfaces/Theme.interface';
import MarginThemeCustomizer from './MarginTheme';
import FontThemeCustomizer from './FontTheme';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../interfaces/TranslationKey';
import TitleThemeCustomizer from './TitleTheme';

interface ThemeCustomizerProps {
    themeLabelKey: string;
    theme: IFontMarginTitleTheme;
    setTheme: (theme: (prev: IFontMarginTitleTheme) => IFontMarginTitleTheme) => void;
    setShouldUpdateThemed: (value: boolean) => void;
}

const FontMarginTitleThemeCustomizer = (props: ThemeCustomizerProps): React.ReactElement => {
    const { t } = useTranslation();

    const handleFontThemeChange = (key: keyof IFontTheme, value: string) => {
        handleThemeChange('font', key, value);
    };

    const handleMarginThemeChange = (key: keyof IMarginTheme, value: number) => {
        handleThemeChange('margin', key, value);
    };

    const handleTitleThemeChange = (key: keyof ITitleTheme, value: ThemeValue) => {
        handleThemeChange('title', key, value);
    };

    const handleThemeChange = <T extends keyof IFontMarginTitleTheme, K extends keyof IFontMarginTitleTheme[T]>(
        section: T,
        key: K,
        value: ThemeValue,
    ) => {
        props.setTheme((prev: IFontMarginTitleTheme) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [key]: value,
            },
        }));

        props.setShouldUpdateThemed(true);
    };

    return (
        <Box>
            <Typography variant="h6">{t(TranslationKey.theme.title)}</Typography>
            <Grid2 container spacing={2} sx={{ my: '10px' }}>
                <Grid2 size={12}>
                    <TitleThemeCustomizer
                        labelKey={'title-' + props.themeLabelKey}
                        theme={props.theme.title}
                        handleThemeChange={handleTitleThemeChange}
                    />
                </Grid2>
            </Grid2>
            <Divider sx={{ my: 2 }} />

            <Typography variant="h6">{t(props.themeLabelKey)}</Typography>
            <Grid2 container spacing={2} sx={{ mb: '10px' }}>
                <Grid2 size={12}>
                    <FontThemeCustomizer
                        labelKey={props.themeLabelKey}
                        theme={props.theme.font}
                        handleThemeChange={handleFontThemeChange}
                        variantItems={[
                            <MenuItem key={`${props.themeLabelKey}-font-variant-h6`} value="h6">
                                {t(TranslationKey.theme.fontSize.L)}
                            </MenuItem>,
                            <MenuItem key={`${props.themeLabelKey}-font-variant-body1`} value="body1">
                                {t(TranslationKey.theme.fontSize.M)}
                            </MenuItem>,
                            <MenuItem key={`${props.themeLabelKey}-font-variant-body2`} value="body2">
                                {t(TranslationKey.theme.fontSize.S)}
                            </MenuItem>,
                            <MenuItem key={`${props.themeLabelKey}-font-variant-caption`} value="caption">
                                {t(TranslationKey.theme.fontSize.XS)}
                            </MenuItem>,
                        ]}
                    />
                </Grid2>
            </Grid2>
            <Divider sx={{ my: 2 }} />

            <Grid2 container spacing={2}>
                <Grid2 size={12}>
                    <MarginThemeCustomizer theme={props.theme.margin} handleThemeChange={handleMarginThemeChange} />
                </Grid2>
            </Grid2>
        </Box>
    );
};

export default FontMarginTitleThemeCustomizer;
